import React from 'react';
import {StyleSheet, Text, View} from 'react-native';

import Styles from '../../Styles';
import Localized from 'src/constants/AppStrings';
import {DNA} from 'src/types/dnaTypes';
import AccountStore from 'src/stores/AccountStore';
import {MenuProductType} from 'src/types/Menu';
import AVFormattedCurrency from '../../elements/AVFormattedCurrency';
import {scanAndPayTemplate} from 'src/components/screens/scanAndPay/templates/scanandPayThemeHelper';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';

type SearchProductItemProps = {
  imageUrl?: string;
  name: string;
  price: number;
  item?: MenuProductType;
  dnaConfiguration?: DNA;
  onPress: () => void;
  isProductCat?: boolean;
  rewardpoints?: number;
};

function SearchProductItem(props: SearchProductItemProps) {
  const calories = props.item?.calories;

  const unitDisplayName = props?.dnaConfiguration?.showCaloriesAsKilocalories
    ? Localized.Labels.kcal
    : Localized.Labels.cal;

  return (
    <View
      accessible={true}
      accessibilityRole="button"
      accessibilityHint="Double tap to navigate to the product details screen"
      accessibilityState={{disabled: false}}
      aria-disabled={false}
      role="button"
      style={scanAndPayTemplate.checkoutStyles.searchProductContainer}
    >
      <View style={styles.leftItem}>
        <Text
          accessible={true}
          accessibilityLabel={`${props.name}, `}
          accessibilityRole="text"
          aria-label={`${props.name}, `}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
          style={styles.productName}
        >
          {props.name}

          {Boolean(
            props.dnaConfiguration?.showCalories && calories !== null,
          ) && (
            <Text
              accessible={true}
              accessibilityLabel={`${calories} ${Localized.Labels.calories.toLowerCase()}, `}
              accessibilityRole="text"
              aria-label={`${calories} ${Localized.Labels.calories.toLowerCase()}, text, `}
              maxFontSizeMultiplier={2}
            >
              {` ${calories} ${unitDisplayName}`}
            </Text>
          )}
        </Text>
        {props?.rewardpoints > 0 && (
          <Text
            accessible={true}
            accessibilityLabel={`${props.name}, `}
            accessibilityRole="text"
            aria-label={`${props.name}, `}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
            style={styles.rewardPrice}
          >
            {props.rewardpoints} {Localized.Labels.points.toLowerCase()}
          </Text>
        )}
      </View>
      <View
        style={{
          flex: 0.4,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end', // Pushes content to the right end
          columnGap: 3, // Optional: React Native 0.71+ for spacing between children
        }}
      >
        <AVFormattedCurrency
          style={styles.productPrice}
          value={props.price}
          accessible={true}
          aria-label={`$${props.price.toFixed(2)}, `}
          accessibilityLabel={`$${props.price.toFixed(2)}, text, `}
          currency={AccountStore.getCurrency()}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
          maxFractionDigits={2}
        />

        <RoundedAppButton
          buttonType={RoundedAppButtonType.Outline}
          buttonViewStyle={{
            overflow: 'hidden',
            borderRadius: 32,
            alignSelf: 'center',
            minWidth: 27,
            maxHeight: 36,
            borderWidth: 1.2,
            backgroundColor: Styles.Colors.PayNew.white01,
          }}
          buttonContainerStyle={{
            alignSelf: 'center',
          }}
          accessibilityLabelValue={Localized.Buttons.add}
          titleText={Localized.Buttons.add}
          textStyle={styles.addButtonText}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
          aria-label={Localized.Buttons.add}
          onPress={props.onPress}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  productPrice: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    fontFamily: scanAndPayTemplate.textFontFamily,
    color: Styles.Colors.PayNew.black01,
    marginRight: Styles.Spacing.m2,
  },
  rewardPrice: {
    fontSize: Styles.Fonts.f8,
    fontWeight: '400',
    fontFamily: scanAndPayTemplate.textFontFamily,
    color: scanAndPayTemplate.rewardsPayColor,
    marginRight: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m1 - 2,
  },
  productName: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    fontFamily: scanAndPayTemplate.textFontFamily,
    color: Styles.Colors.PayNew.black01,
    marginRight: Styles.Spacing.m2,
  },
  addButtonText: {
    fontSize: Styles.Fonts.f1 - 2,
    fontWeight: '700',
    textAlign: 'center',
  },
  leftItem: {
    flex: 0.6,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
});

export default SearchProductItem;
